import i18next, { FormatFunction, InterpolationOptions, PostProcessorModule, Resource } from "i18next";
import intervalPlural from "i18next-intervalplural-postprocessor";

export const DEFAULT_LANGUAGE = "en-GB";

// This string is designed to render acceptably (in English anyway), but it should never appear as it should be filtered out by something like the optional formatting postprocessor. To that end it contains a zero-width non-breaking space so that we can identify it easily and uniquely.
export const NONE_PLACEHOLDER = "(none)";

// This function formats values such as dates and numbers when they appear in translatable strings. It will run by default on any interpolated value (ie, the number in "you have {{count}} items") and the translation key reads "you have {{count, text}} items" then `format` will be set to "text". See https://www.i18next.com/translation-function/formatting for more
const format: FormatFunction = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any, // The value to be formatted
  format?: string, // The string passed in with it (if any)
  lng?: string, // The language we are translating into
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options?: InterpolationOptions & { [key: string]: any },
) => {
  // Format numbers according to the appropriate in-built locale rules — using digit separators, etc.
  if (typeof value === "number") {
    try {
      return value.toLocaleString(lng);
    } catch (e) {
      // handle case when locale is unsupported by this browser, default to user's locale
      if (e instanceof Error && e.name === "RangeError") {
        return value.toLocaleString();
      }
      throw e;
    }
  }

  // Slight hack here: the optional postprocessor uses | as a special character so let's quietly strip those out if we find them.
  if (typeof value === "string") {
    return value.replace(/\|/g, "");
  }

  // Fallback
  return value?.toString() ?? NONE_PLACEHOLDER;
};

export default async function initialiseTranslation(postProcessors: PostProcessorModule[], resources: Resource) {
  i18next.use(intervalPlural);
  for (const module of postProcessors) i18next.use(module);
  await i18next.init({
    resources,
    fallbackLng: DEFAULT_LANGUAGE,
    interpolation: { format, alwaysFormat: true },
  });
}
